<template>
  <div ref="campo" class="fecha">
    <button type="button" class="fecha__campo" :class="{ 'fecha__campo--vacio': !modelValue }" @click="alternar">
      <i class="mdi mdi-calendar"></i>
      <span>{{ modelValue ? mostrar(modelValue) : placeholder }}</span>
    </button>
    <!-- Lleva el `required` al formulario: un campo readonly no se valida. -->
    <input class="fecha__validacion" :value="modelValue" :required="required" tabindex="-1" aria-hidden="true" @focus="abrir">

    <!-- click.prevent: dentro de un <label>, un clic en el panel no reactiva el campo. -->
    <div v-if="abierto" ref="panel" class="fecha__panel" :style="posicion" @mousedown.stop @click.prevent>
      <header class="fecha__mes">
        <button type="button" title="Mes anterior" @click="mover(-1)">‹</button>
        <strong>{{ tituloMes }}</strong>
        <button type="button" title="Mes siguiente" @click="mover(1)">›</button>
      </header>
      <div class="fecha__grilla">
        <span v-for="d in DIAS" :key="d" class="fecha__dia-semana">{{ d }}</span>
        <button
          v-for="(c, i) in celdas"
          :key="i"
          type="button"
          class="fecha__dia"
          :class="{
            'fecha__dia--fuera': !c.delMes,
            'fecha__dia--hoy': c.iso === hoy,
            'fecha__dia--elegido': c.iso === modelValue,
          }"
          @click="elegir(c.iso)"
        >{{ c.dia }}</button>
      </div>
      <footer class="fecha__pie">
        <button type="button" @click="elegir(hoy)">Hoy</button>
        <button v-if="!required && modelValue" type="button" @click="elegir('')">Borrar</button>
      </footer>
    </div>
  </div>
</template>

<script>
const DIAS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const MES = new Intl.DateTimeFormat('es-CL', { month: 'long', year: 'numeric' });

// AAAA-MM-DD en hora local: toISOString lo pasaría a UTC y podría correr el día.
function iso(fecha) {
  const p = (n) => String(n).padStart(2, '0');
  return `${fecha.getFullYear()}-${p(fecha.getMonth() + 1)}-${p(fecha.getDate())}`;
}

function desdeIso(texto) {
  const [a, m, d] = texto.split('-').map(Number);
  return new Date(a, m - 1, d);
}

// Selector de fecha del tablero. Su v-model es AAAA-MM-DD, lo mismo que espera la API.
export default {
  name: 'CampoFecha',

  props: {
    modelValue: { type: String, default: '' },
    required: { type: Boolean, default: false },
    placeholder: { type: String, default: 'dd-mm-aaaa' },
  },

  emits: ['update:modelValue'],

  data() {
    return { DIAS, abierto: false, visto: null, posicion: {} };
  },

  computed: {
    hoy() {
      return iso(new Date());
    },
    tituloMes() {
      const texto = MES.format(this.visto);
      return texto.charAt(0).toUpperCase() + texto.slice(1);
    },
    // Seis semanas completas desde el lunes: la grilla no salta de alto entre meses.
    celdas() {
      const primero = new Date(this.visto.getFullYear(), this.visto.getMonth(), 1);
      const inicio = new Date(primero);
      inicio.setDate(1 - ((primero.getDay() + 6) % 7));
      return Array.from({ length: 42 }, (_, i) => {
        const dia = new Date(inicio);
        dia.setDate(inicio.getDate() + i);
        return { iso: iso(dia), dia: dia.getDate(), delMes: dia.getMonth() === this.visto.getMonth() };
      });
    },
  },

  beforeUnmount() {
    this.cerrar();
  },

  methods: {
    mostrar(valor) {
      const [a, m, d] = valor.split('-');
      return `${d}-${m}-${a}`;
    },

    alternar() {
      if (this.abierto) this.cerrar();
      else this.abrir();
    },

    abrir() {
      if (this.abierto) return;
      const base = this.modelValue ? desdeIso(this.modelValue) : new Date();
      this.visto = new Date(base.getFullYear(), base.getMonth(), 1);
      this.ubicar();
      this.abierto = true;
      document.addEventListener('mousedown', this.afuera);
      document.addEventListener('keydown', this.tecla);
      window.addEventListener('resize', this.cerrar);
    },

    cerrar() {
      this.abierto = false;
      document.removeEventListener('mousedown', this.afuera);
      document.removeEventListener('keydown', this.tecla);
      window.removeEventListener('resize', this.cerrar);
    },

    // Fijo a la ventana: dentro de un modal con scroll, uno absoluto quedaría recortado.
    ubicar() {
      const caja = this.$refs.campo.getBoundingClientRect();
      const alto = 300;
      const arriba = caja.bottom + alto > window.innerHeight && caja.top > alto;
      this.posicion = {
        left: `${Math.min(caja.left, window.innerWidth - 270)}px`,
        top: arriba ? `${caja.top - alto - 4}px` : `${caja.bottom + 4}px`,
      };
    },

    afuera(evento) {
      if (!this.$refs.campo.contains(evento.target)) this.cerrar();
    },

    tecla(evento) {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        this.cerrar();
      }
    },

    mover(meses) {
      this.visto = new Date(this.visto.getFullYear(), this.visto.getMonth() + meses, 1);
    },

    elegir(valor) {
      this.$emit('update:modelValue', valor);
      this.cerrar();
    },
  },
};
</script>
